import {
  createContext,
  useState,
  useMemo,
  useCallback,
  FC,
  ReactNode,
  useTransition,
} from "react";
import { OverridedMixpanel } from "mixpanel-browser";
import {
  formatMixpanelEventName,
  MixpanelProperties,
  MixpanelLastTouchProperties,
  MixpanelFirstTouchProperties,
  MIXPANEL_ACTION,
  MIXPANEL_OBJECT,
  MIXPANEL_PAGE,
  MIXPANEL_URL_PARAM,
} from "./mixpanelTypes";
import i18nService from "src/i18n/i18n-service";
import { measureLCP } from "src/utils/performance/webVitals";

// This extension allows Digioh's custom JS API to access the current page for tracking.
declare global {
  interface Window {
    digioh: { currentPage: string };
  }
}

// MixpanelContext provides components with the ability to track analytics with events that follow our standard format.
export const MixpanelContext = createContext<MixpanelContextValues>({});

const getMixpanelURLParams: (
  mixpanel: OverridedMixpanel,
) => MixpanelProperties = mixpanel => {
  const params = Object.values(MIXPANEL_URL_PARAM);
  const urlParams = new URLSearchParams(
    typeof window !== "undefined" ? window.location.search : "",
  );
  const capturedUrlParams: MixpanelProperties = {};

  const defaultCapturedProperties = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_content",
    "utm_term",
  ];
  const lastTouchParams: MixpanelLastTouchProperties = {};
  const firstTouchParams: MixpanelFirstTouchProperties = {};

  params.forEach(param => {
    const capturedValue = urlParams.get(param) || undefined;
    if (capturedValue) {
      // will be registered as a superset metadata for all events
      lastTouchParams[`${param} [last touch]`] = capturedValue;
    }
    // Properties listed in `defaultCapturedProperties` are captured by default, so no need to re-send the data.
    // They do not capture [last touch] superset property by default, which is the reason for their inclusion
    if (!defaultCapturedProperties.includes(param)) {
      capturedUrlParams[param] = capturedValue;
      firstTouchParams[`${param} [first touch]`] = capturedValue;
    }
  });

  mixpanel.register(lastTouchParams);
  // first touch params do not overwrite the previous value, so only their first interaction is stored
  mixpanel.register_once(firstTouchParams);
  return capturedUrlParams;
};

// The MixpanelProvider encapsulates state required for components to fire contextual Mixpanel events without increasing individual component responsibility.
const MixpanelProvider: FC<MixpanelProviderProps> = ({
  mixpanel,
  initialPage,
  children,
}) => {
  const [currentPage, setCurrentPage] = useState<
    MIXPANEL_PAGE | string | undefined
  >(initialPage);
  const [isPageViewTracked, setIsPageViewTracked] = useState(false);
  const [isLinkClickRegistered, setIsLinkClickRegistered] = useState(false);
  const [_isPending, startTransition] = useTransition();

  // The user's locale is captured as a property on every event
  const userLocale = i18nService.getUsersLocale();

  const track: MixpanelTrackingFunction = useCallback(
    (object, action, properties, callback, options = undefined) => {
      const mixpanelUrlParams = getMixpanelURLParams(mixpanel);
      const mixpanelProperties = {
        ...properties,
        ...mixpanelUrlParams,
        user_locale: userLocale,
        platform: "aws",
      };
      if (currentPage || options?.defaultPageName) {
        let eventName;
        const activePage = currentPage ? currentPage : options?.defaultPageName;
        if (options && options.omitPageName) {
          eventName = `${object} ${action}`;
        } else {
          eventName = formatMixpanelEventName(activePage || "", object, action);
        }
        mixpanel.track(
          eventName,
          mixpanelProperties,
          callback ? { send_immediately: true } : undefined,
          callback,
        );
      } else {
        // If currentPage is not yet set, we can't send the event, but we must
        // still invoke the callback so consumers (e.g. navigation) aren't blocked.
        callback?.();
      }
    },
    [mixpanel, currentPage, userLocale],
  );

  const trackPageView: MixpanelTrackingPageViewFunction = useCallback(
    (loadedPage, properties) => {
      if (loadedPage !== currentPage) {
        startTransition(() => {
          setCurrentPage(loadedPage);
          if (typeof window != "undefined") {
            window.digioh = { currentPage: loadedPage }; // Add the current page to digioh context
          }
          // ensure page view event is only tracked once per page
          setIsPageViewTracked(false);
        });
      }
      if (isPageViewTracked) return;

      const mixpanelUrlParams = getMixpanelURLParams(mixpanel);
      const mixpanelProperties = {
        ...properties,
        ...mixpanelUrlParams,
        user_locale: userLocale,
        platform: "aws",
      };

      // Track initial page view immediately (without LCP to avoid blocking)
      mixpanel.track(
        formatMixpanelEventName(
          loadedPage,
          MIXPANEL_OBJECT.PAGE,
          MIXPANEL_ACTION.LOADED,
        ),
        mixpanelProperties,
      );

      // Measure LCP asynchronously and send as a separate event to avoid affecting page performance
      // All operations are non-blocking to ensure no performance impact
      try {
        // Check if page is visible to improve LCP capture rate - non-blocking
        const isPageVisible =
          typeof document !== "undefined" &&
          document.visibilityState === "visible";

        measureLCP(
          lcpResult => {
            try {
              // Validate LCP result before tracking - non-blocking
              if (typeof lcpResult?.value !== "number" || lcpResult.value < 0) {
                console.warn("Invalid LCP result for tracking:", lcpResult);
                return;
              }

              const lcpProperties = {
                ...mixpanelProperties,
                lcp_value: lcpResult.value,
                page_visible_on_load: isPageVisible,
                measurement_type: lcpResult.measurementType,
                // Add error message if present for debugging
                ...(lcpResult.errorMessage && {
                  error_message: lcpResult.errorMessage,
                }),
              };

              // Track LCP as a separate performance event - non-blocking
              mixpanel.track(
                formatMixpanelEventName(
                  loadedPage,
                  MIXPANEL_OBJECT.PAGE,
                  MIXPANEL_ACTION.PERFORMANCE_MEASURED,
                ),
                lcpProperties,
              );
            } catch (trackingError) {
              // Prevent tracking errors from breaking the page - non-blocking
              const errorMsg = `LCP tracking error: ${
                trackingError instanceof Error
                  ? trackingError.message
                  : String(trackingError)
              }`;
              console.warn(errorMsg);

              // Still try to track with error info - non-blocking
              try {
                mixpanel.track(
                  formatMixpanelEventName(
                    loadedPage,
                    MIXPANEL_OBJECT.PAGE,
                    MIXPANEL_ACTION.PERFORMANCE_MEASURED,
                  ),
                  {
                    ...mixpanelProperties,
                    lcp_value: lcpResult?.value || 3000,
                    measurement_type: "tracking_failed",
                    error_message: errorMsg,
                    page_visible_on_load: isPageVisible,
                  },
                );
              } catch (secondaryError) {
                console.warn(
                  "Secondary LCP tracking also failed:",
                  secondaryError,
                );
              }
            }
          },
          {
            // Report all changes to capture more events, especially for dynamic content
            reportAllChanges: true,
            // Set timeout to ensure we get some data even if LCP never finalizes
            timeout: 8000,
            // Provide fallback value for cases where LCP cannot be measured
            fallbackValue: isPageVisible ? 2500 : 5000, // Higher fallback for background tabs
          },
        );
      } catch (measurementError) {
        // Prevent LCP measurement errors from breaking the page - non-blocking
        const errorMsg = `LCP measurement setup error: ${
          measurementError instanceof Error
            ? measurementError.message
            : String(measurementError)
        }`;
        console.warn(errorMsg);

        // Track a fallback performance event to maintain data consistency - non-blocking
        try {
          const fallbackProperties = {
            ...mixpanelProperties,
            lcp_value: 3000, // Conservative fallback
            measurement_type: "measurement_failed",
            error_message: errorMsg,
            page_visible_on_load:
              typeof document !== "undefined" &&
              document.visibilityState === "visible",
          };

          mixpanel.track(
            formatMixpanelEventName(
              loadedPage,
              MIXPANEL_OBJECT.PAGE,
              MIXPANEL_ACTION.PERFORMANCE_MEASURED,
            ),
            fallbackProperties,
          );
        } catch (fallbackError) {
          const fallbackErrorMsg = `Fallback LCP tracking also failed: ${
            fallbackError instanceof Error
              ? fallbackError.message
              : String(fallbackError)
          }`;
          console.warn(fallbackErrorMsg);
        }
      }

      startTransition(() => {
        setIsPageViewTracked(true);
      });
    },
    [currentPage, mixpanel, isPageViewTracked, userLocale],
  );

  const registerLinkClick: MixpanelTrackingLinkClickFunction = useCallback(
    (selector, eventName, properties, forceInitialize = false) => {
      if (isLinkClickRegistered && !forceInitialize) return;
      setIsLinkClickRegistered(true);

      mixpanel.track_links(selector, eventName, properties);
    },
    [mixpanel, isLinkClickRegistered],
  );

  const registerSplitExperiment: MixpanelRegisteringSplitExperimentFunction =
    useCallback(
      (splitName, treatment) => {
        mixpanel.register({ [splitName]: treatment }, 1);
      },
      [mixpanel],
    );

  const unregisterSplitExperiment: MixpanelUnregisteringSplitExperimentFunction =
    useCallback(
      splitName => {
        mixpanel.unregister(splitName);
      },
      [mixpanel],
    );

  const getProperty: MixpanelGetPropertyFunction = useCallback(
    propertyName => {
      return mixpanel.get_property(propertyName)
        ? (mixpanel.get_property(propertyName) as string)
        : "";
    },
    [mixpanel],
  );

  const getDistinctId: MixpanelGetDistinctIdFunction = useCallback(() => {
    // get_distinct_id() reads from the persistence layer which is only set up
    // after init(). If the SDK has not been initialised yet (e.g. during SSR or
    // if the head stub's init is still queued), guard against the crash rather
    // than propagating a TypeError up to the React render.
    try {
      return mixpanel.get_distinct_id() as string;
    } catch {
      return "";
    }
  }, [mixpanel]);

  const registerProperty: MixpanelRegisteringPropertyFunction = useCallback(
    (key, value) => {
      mixpanel.register({ [key]: value }, 1);
    },
    [mixpanel],
  );

  const setPage = useCallback((page: MIXPANEL_PAGE | string) => {
    startTransition(() => {
      setCurrentPage(page);
    });
  }, []);

  const contextValues = useMemo(() => {
    return {
      track,
      trackPageView,
      registerLinkClick,
      registerSplitExperiment,
      unregisterSplitExperiment,
      getProperty,
      registerProperty,
      getDistinctId,
      setPage,
    };
  }, [
    track,
    registerLinkClick,
    trackPageView,
    registerSplitExperiment,
    unregisterSplitExperiment,
    getProperty,
    registerProperty,
    getDistinctId,
    setPage,
  ]);

  return (
    <MixpanelContext.Provider value={contextValues}>
      {children}
    </MixpanelContext.Provider>
  );
};

// Function to wrap a ReactNode with a MixpanelProvider based on provided options. Intended to be called within the Gatsby config APIs.
export const wrapWithMixpanel = (
  mixpanel: OverridedMixpanel,
  nodeEnv: string,
  mixpanelToken: string,
  initialPage?: MIXPANEL_PAGE,
) => {
  return ({ element }: { element: ReactNode }) => {
    if (!mixpanelToken) {
      mixpanelToken = "DUMMY_TOKEN"; // Mixpanel needs to have a non-empty token value on init for its functions to not throw errors
      console.error("No Mixpanel token provided");
    }
    // Always call init() on the npm MixpanelLib instance. The instance bundled
    // by webpack is separate from window.mixpanel (the head stub); it must be
    // initialised before any SDK method (track, register, get_distinct_id, etc.)
    // is called. Mixpanel's init() is idempotent — if __loaded is already true
    // (e.g. the stub's queued init was processed by init_lib() at module load)
    // init() is a no-op and batch_requests: false set by the head stub is not
    // overridden.
    mixpanel.init(mixpanelToken, {
      debug: nodeEnv !== "production",
    });
    return (
      <MixpanelProvider mixpanel={mixpanel} initialPage={initialPage}>
        {element}
      </MixpanelProvider>
    );
  };
};

type MixpanelTrackingOptions = {
  omitPageName?: boolean;
  defaultPageName?: string;
};

export type MixpanelTrackingFunction = (
  object: MIXPANEL_OBJECT,
  action: MIXPANEL_ACTION,
  properties?: MixpanelProperties,
  callback?: () => void,
  options?: MixpanelTrackingOptions,
) => void;

type MixpanelTrackingPageViewFunction = (
  loadedPage: MIXPANEL_PAGE | string,
  properties?: MixpanelProperties,
) => void;

export type MixpanelTrackingLinkClickFunction = (
  selector: string,
  eventName: string,
  properties?: MixpanelProperties | ((ele: HTMLElement) => MixpanelProperties),
  forceInitialize?: boolean,
) => void;

export type MixpanelRegisteringSplitExperimentFunction = (
  splitName: string,
  treatment: string,
) => void;

type MixpanelRegisteringPropertyFunction = (key: string, value: string) => void;

export type MixpanelUnregisteringSplitExperimentFunction = (
  splitName: string,
) => void;

export type MixpanelGetPropertyFunction = (propertyName: string) => string;

export type MixpanelGetDistinctIdFunction = () => string;

export type MixpanelSetPageFunction = (page: MIXPANEL_PAGE | string) => void;

// MixpanelContextValues defines the values available to context consumers.
interface MixpanelContextValues {
  track?: MixpanelTrackingFunction;
  trackPageView?: MixpanelTrackingPageViewFunction;
  registerLinkClick?: MixpanelTrackingLinkClickFunction;
  registerSplitExperiment?: MixpanelRegisteringSplitExperimentFunction;
  unregisterSplitExperiment?: MixpanelUnregisteringSplitExperimentFunction;
  getProperty?: MixpanelGetPropertyFunction;
  registerProperty?: MixpanelRegisteringPropertyFunction;
  getDistinctId?: MixpanelGetDistinctIdFunction;
  setPage?: MixpanelSetPageFunction;
}

// MixpanelProviderProps defines the props required to initialize the MixpanelProvider.
interface MixpanelProviderProps {
  mixpanel: OverridedMixpanel;
  initialPage?: MIXPANEL_PAGE;
  children?: ReactNode;
}
